import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Server, Globe, Router, Camera, Monitor, Box, Wifi, Network,
  Sparkles, Loader2, CheckCircle2, AlertCircle, Power,
  Clock, Users, Wrench, Terminal, Activity, ChevronDown, ChevronRight,
  Plus, Trash2, RefreshCw, ShieldQuestion,
} from "lucide-react";
import {
  useDevice, useScoutDevice, useDecideProposal, useConnectorOutputs,
  useDeleteConnector, useVerifyConnector,
  useDeleteDevice, useSyncDeviceName,
  type Device, type CapabilityProposal, type DeviceKind, type DeviceConnector,
  type ConnectorOutput, type ProposalSeverity, type VerifyResult,
} from "@/lib/queries";
import { AddConnectorSheet, VerifyResultPanel } from "@/components/AddConnectorSheet";
import { cn } from "@/lib/utils";

const KIND_ICON: Record<DeviceKind, typeof Server> = {
  linux:        Server,
  cpanel:       Globe,
  mikrotik:     Router,
  hikvision:    Camera,
  windows:      Monitor,
  unifi_ap:     Wifi,
  unifi_switch: Network,
  other:        Box,
};

const CONNECTOR_LABEL: Record<string, string> = {
  ssh: "SSH", filebeat: "Filebeat", zabbix_agent: "Zabbix",
  netflow: "NetFlow", snmp: "SNMP", api: "API", http_digest: "HTTP",
  shell_command: "Shell", http_poll: "HTTP poll", log_tail: "Log tail",
};

const CONNECTOR_TONE: Record<string, string> = {
  active:      "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  configuring: "border-amber-500/40   bg-amber-500/10   text-amber-700   dark:text-amber-400",
  pending:     "border-border         bg-muted          text-muted-foreground",
  failing:     "border-red-500/40     bg-red-500/10     text-red-600     dark:text-red-400",
  disabled:    "border-border         bg-muted/40       text-muted-foreground",
};

const SEVERITY_TONE: Record<ProposalSeverity, string> = {
  high:   "border-red-500/40    bg-red-500/10    text-red-600    dark:text-red-400",
  medium: "border-amber-500/40  bg-amber-500/10  text-amber-700  dark:text-amber-400",
  low:    "border-blue-500/40   bg-blue-500/10   text-blue-700   dark:text-blue-400",
  info:   "border-border        bg-muted         text-muted-foreground",
};

function ConnectorChip({ c }: { c: DeviceConnector }) {
  const tone = CONNECTOR_TONE[c.status] ?? CONNECTOR_TONE.pending;
  const Icon =
    c.status === "active"      ? CheckCircle2 :
    c.status === "failing"     ? AlertCircle :
    c.status === "configuring" ? Loader2 : Power;
  return (
    <span
      title={c.last_error ? `${c.kind}: ${c.last_error}` : `${c.kind}: ${c.status}`}
      className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium", tone)}
    >
      <Icon className={cn("h-3 w-3", c.status === "configuring" && "animate-spin")} />
      {CONNECTOR_LABEL[c.kind] ?? c.kind}
    </span>
  );
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  const dt = (Date.now() - t) / 1000;
  if (dt < 60)        return "just now";
  if (dt < 3600)      return `${Math.floor(dt / 60)}m ago`;
  if (dt < 86_400)    return `${Math.floor(dt / 3600)}h ago`;
  return `${Math.floor(dt / 86_400)}d ago`;
}

function ProposalCard({
  p, deviceId,
}: {
  p: CapabilityProposal;
  deviceId: number;
}) {
  const decide = useDecideProposal();
  const sev: ProposalSeverity = p.severity_if_skipped ?? "low";
  const sevTone = SEVERITY_TONE[sev];

  const [working, setWorking] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const onDecide = async (decision: "accepted" | "skipped" | "deferred") => {
    setWorking(decision);
    setErrorMsg(null);
    try {
      await decide.mutateAsync({ deviceId, proposalId: p.id, body: { decision } });
      // On success the device query is invalidated by useDecideProposal,
      // so this card unmounts (proposal moves out of pending) — no need
      // to clear local state.
    } catch (e) {
      setErrorMsg(
        e instanceof Error ? e.message : "Provisioning failed",
      );
    } finally {
      setWorking(null);
    }
  };

  // Render config as a compact preview. We don't know the shape until the
  // scout proposes it — use a generic key:value print, truncated.
  const configPreview = useMemo(() => {
    const lines = Object.entries(p.config).map(([k, v]) =>
      `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`
    );
    return lines.join("\n");
  }, [p.config]);

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold">{p.title}</h3>
            <span className={cn(
              "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide",
              sevTone,
            )}>
              {sev}
            </span>
            <span className="rounded-full border border-border bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
              {CONNECTOR_LABEL[p.kind] ?? p.kind}
            </span>
          </div>
          <p className="mt-2 text-sm text-muted-foreground whitespace-pre-line">{p.rationale}</p>
          {configPreview && (
            <pre className="mt-2 overflow-x-auto rounded-md border border-border bg-muted/40 p-2 font-mono text-[11px]">
              {configPreview}
            </pre>
          )}
          {errorMsg && (
            <div className="mt-2 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-400">
              {errorMsg}
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          disabled={working !== null}
          onClick={() => onDecide("deferred")}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent disabled:opacity-50"
        >
          {working === "deferred" ? <Loader2 className="h-3 w-3 animate-spin" /> : "Defer"}
        </button>
        <button
          type="button"
          disabled={working !== null}
          onClick={() => onDecide("skipped")}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent disabled:opacity-50"
        >
          {working === "skipped" ? <Loader2 className="h-3 w-3 animate-spin" /> : "Skip"}
        </button>
        <button
          type="button"
          disabled={working !== null}
          onClick={() => onDecide("accepted")}
          title={
            p.kind === "log_tail"
              ? "Add these paths to filebeat and restart the shipper."
              : "Record the decision. Provisioning for this kind isn't shipped yet."
          }
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {working === "accepted" ? <Loader2 className="h-3 w-3 animate-spin" /> : "Accept"}
        </button>
      </div>
    </div>
  );
}

function ConnectorRow({
  device, c,
}: {
  device: Device;
  c: DeviceConnector;
}) {
  const verify = useVerifyConnector();
  const del = useDeleteConnector();
  const [open, setOpen] = useState(false);
  // Local capture of the latest probe result so we render evidence
  // immediately after Verify. The connector row itself reflects status
  // via the useDevice query refetch.
  const [latestProbe, setLatestProbe] = useState<VerifyResult | null>(null);

  const tone =
    c.status === "active"      ? "text-emerald-700 dark:text-emerald-400" :
    c.status === "failing"     ? "text-red-600   dark:text-red-400" :
    c.status === "configuring" ? "text-amber-700 dark:text-amber-400" :
                                  "text-muted-foreground";

  const onVerify = async () => {
    if (verify.isPending || c.id === undefined) return;
    try {
      const r = await verify.mutateAsync({ deviceId: device.id, connectorId: c.id });
      setLatestProbe(r.verify);
      setOpen(true);  // expand to show the result
    } catch (e) {
      setLatestProbe({
        ok: false,
        message: e instanceof Error ? e.message : "verify failed",
      });
      setOpen(true);
    }
  };

  const onDelete = async () => {
    if (c.id === undefined) return;
    if (!confirm(`Remove the ${c.kind} connector from ${device.name}? Panel record only — Tyrael won't touch the host.`)) return;
    try {
      await del.mutateAsync({ deviceId: device.id, connectorId: c.id });
    } catch (e) {
      alert(e instanceof Error ? e.message : "Couldn't delete");
    }
  };

  const Chevron = open ? ChevronDown : ChevronRight;
  const cfgEntries = Object.entries((c.config ?? {}) as Record<string, unknown>);

  return (
    <div>
      <div className="flex items-start gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-0.5 rounded-md p-0.5 text-muted-foreground hover:bg-accent"
          title={open ? "Collapse" : "Expand"}
        >
          <Chevron className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm">{c.kind}</span>
            <span className={cn("text-[10px] font-medium uppercase tracking-wide", tone)}>
              {c.status}
            </span>
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {c.last_ok_at
              ? `last ok ${new Date(c.last_ok_at).toLocaleString()}`
              : c.last_check_at
                ? `last check ${new Date(c.last_check_at).toLocaleString()}`
                : "never checked"}
          </div>
          {c.last_error && (
            <div className="mt-1 truncate text-[11px] text-red-700 dark:text-red-400" title={c.last_error}>
              {c.last_error}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onVerify}
            disabled={verify.isPending || c.id === undefined}
            title="Re-probe this connector"
            className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-background px-2 text-[11px] hover:bg-accent disabled:opacity-50"
          >
            {verify.isPending
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <RefreshCw className="h-3 w-3" />}
            Verify
          </button>
          {c.id !== undefined && (
            <button
              type="button"
              onClick={onDelete}
              title="Remove this connector"
              className="rounded-md p-1 text-muted-foreground hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="space-y-3 border-t border-border bg-muted/20 px-3 py-3">
          {/* What this connector does — the configured target */}
          <div>
            <div className="mb-1 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              <ShieldQuestion className="h-3 w-3" />
              What this connector does
            </div>
            {cfgEntries.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">
                No config — connector is metadata only.
              </p>
            ) : (
              <pre className="overflow-x-auto rounded-md border border-border bg-background p-2 font-mono text-[11px]">
                {JSON.stringify(c.config, null, 2)}
              </pre>
            )}
          </div>

          {/* Last verify probe result, if we just ran one */}
          {latestProbe && (
            <div>
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Latest probe
              </div>
              <VerifyResultPanel
                result={latestProbe}
                onDone={() => setLatestProbe(null)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ConnectorsSection({
  device, onAdd,
}: {
  device: Device;
  onAdd: () => void;
}) {
  const connectors = device.connectors ?? [];

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Connectors</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            How Tyrael talks to this device. Click a row to see what it's pointed at,
            or hit Verify to re-probe — green status only ever reflects a real probe.
          </p>
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> Add connector
        </button>
      </div>

      {connectors.length === 0 ? (
        <div className="mt-4 rounded-md border border-dashed border-border bg-muted/20 p-6 text-center">
          <p className="text-sm text-muted-foreground">
            No connectors yet. Click <strong>Add connector</strong> to attach one.
          </p>
        </div>
      ) : (
        <div className="mt-4 divide-y divide-border rounded-md border border-border">
          {connectors.map((c) => (
            <ConnectorRow key={c.id ?? c.kind} device={device} c={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function DeviceHeader({ device, onBack }: { device: Device; onBack: () => void }) {
  const Icon = KIND_ICON[device.kind] ?? Box;
  const noConnectors = device.connectors.length === 0;
  const syncName = useSyncDeviceName();
  const delDevice = useDeleteDevice();

  // Only kinds that have an SSH path can self-report a hostname.
  // Hikvision is pure HTTP — no SSH session, no `hostname` to call.
  // Pulled out as a constant so the button stays consistent with the
  // backend's _SYNC_NAME_PROBES dict.
  const canSyncName = device.kind !== "hikvision";

  async function onSyncName() {
    if (syncName.isPending) return;
    try {
      const result = await syncName.mutateAsync(device.id);
      if (result.changed) {
        // Soft feedback — the React Query invalidate refetches the
        // device row, so the displayed name updates inline.
      }
    } catch (e) {
      // Render the actual server detail (FastAPI {detail: "..."}).
      // ApiError stores the body string; parse defensively.
      const err = e as { body?: unknown; message?: string };
      let msg = err.message ?? "sync failed";
      if (typeof err.body === "string") {
        try { msg = (JSON.parse(err.body) as { detail?: string }).detail ?? msg; } catch { /* not JSON */ }
      }
      alert(`Sync failed: ${msg}`);
    }
  }

  async function onDelete() {
    if (delDevice.isPending) return;
    if (!confirm(
      `Permanently delete ${device.name}?\n\n` +
      `This removes the device row, all its connectors, ` +
      `all command outputs, and all capability proposals. ` +
      `Any device that proxy-jumped through this one becomes unreachable ` +
      `until you give it a new jump host.\n\n` +
      `Cannot be undone.`
    )) return;
    try {
      await delDevice.mutateAsync(device.id);
      onBack();
    } catch (e) {
      const err = e as { body?: unknown; message?: string };
      let msg = err.message ?? "delete failed";
      if (typeof err.body === "string") {
        try { msg = (JSON.parse(err.body) as { detail?: string }).detail ?? msg; } catch { /* not JSON */ }
      }
      alert(`Delete failed: ${msg}`);
    }
  }

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> All devices
      </button>
      <div className="flex items-start gap-4">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-7 w-7" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <h1 className="truncate text-2xl font-semibold tracking-tight">{device.name}</h1>
            <div className="flex shrink-0 items-center gap-1.5">
              {canSyncName && (
                <button
                  type="button"
                  onClick={onSyncName}
                  disabled={syncName.isPending}
                  title="Read the device's own hostname over SSH and set this device's name to it. Useful when you added the device with a placeholder name."
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs hover:bg-accent disabled:opacity-50"
                >
                  <RefreshCw className={cn("h-3.5 w-3.5", syncName.isPending && "animate-spin")} />
                  {syncName.isPending ? "Syncing…" : "Sync name"}
                </button>
              )}
              <button
                type="button"
                onClick={onDelete}
                disabled={delDevice.isPending}
                title="Permanently delete this device and all its connectors."
                className="inline-flex items-center gap-1.5 rounded-md border border-red-500/30 bg-red-500/5 px-2.5 py-1.5 text-xs text-red-600 hover:bg-red-500/10 disabled:opacity-50 dark:text-red-400"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {delDevice.isPending ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
          <p className="truncate text-sm text-muted-foreground">
            {device.role ?? <span className="italic">no role set</span>}
            <span className="px-2">·</span>
            <span className="font-mono">{device.ssh_user}@{device.hostname ?? device.name}:{device.ssh_port}</span>
            <span className="px-2">·</span>
            <span className="font-mono uppercase">{device.kind}</span>
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {noConnectors && <span className="text-[11px] text-muted-foreground">No connectors yet</span>}
            {device.connectors.map((c, i) => <ConnectorChip key={i} c={c} />)}
          </div>
        </div>
      </div>
    </div>
  );
}

function ScoutPanel({ device }: { device: Device }) {
  const scout = useScoutDevice();
  const summary = device.scout_summary ?? {};
  const hasSummary = !!(summary.detected_role || (summary.detected_services?.length));
  const services = summary.detected_services ?? [];
  const users = summary.detected_users ?? [];

  const onScout = () => {
    if (scout.isPending) return;
    scout.mutate(device.id);
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <Sparkles className="h-4 w-4 text-primary" />
            Capability scout
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Tyrael SSHes in, looks at what's running, and proposes monitoring you should add.
            {device.last_scouted_at ? (
              <> Last run <Clock className="inline h-3 w-3" /> {relativeTime(device.last_scouted_at)}.</>
            ) : (
              <> Never run on this device.</>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={onScout}
          disabled={scout.isPending}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {scout.isPending
            ? <><Loader2 className="h-4 w-4 animate-spin" /> Scouting…</>
            : <><Sparkles className="h-4 w-4" /> {device.last_scouted_at ? "Re-scout" : "Discover capabilities"}</>}
        </button>
      </div>

      {scout.isError && (
        <div className="mt-3 rounded-md border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-700 dark:text-red-400">
          Scout failed: {(scout.error as Error)?.message ?? "unknown error"}
        </div>
      )}

      {hasSummary && (
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <div className="mb-1 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              <Wrench className="h-3 w-3" /> Detected role
            </div>
            <div className="text-sm">{summary.detected_role || <span className="text-muted-foreground">—</span>}</div>
            {summary.notes && (
              <p className="mt-2 text-xs text-muted-foreground">{summary.notes}</p>
            )}
          </div>
          <div className="space-y-3">
            {services.length > 0 && (
              <div>
                <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Services seen
                </div>
                <div className="flex flex-wrap gap-1">
                  {services.map((s, i) => (
                    <span key={i} className="rounded-full border border-border bg-muted px-2 py-0.5 font-mono text-[11px]">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {users.length > 0 && (
              <div>
                <div className="mb-1 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  <Users className="h-3 w-3" /> Users
                </div>
                <div className="flex flex-wrap gap-1">
                  {users.map((u, i) => (
                    <span key={i} className="rounded-full border border-border bg-muted px-2 py-0.5 font-mono text-[11px]">
                      {u}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function OutputRow({ o }: { o: ConnectorOutput }) {
  const [open, setOpen] = useState(false);
  const truncated = o.output_full_len !== undefined && o.output_full_len > o.output.length;
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-accent/40"
      >
        <Chevron className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span
          className={cn(
            "h-1.5 w-1.5 shrink-0 rounded-full",
            o.ok ? "bg-emerald-500" : "bg-red-500",
          )}
        />
        <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
          {new Date(o.ran_at).toLocaleString(undefined, {
            month: "short", day: "2-digit",
            hour: "2-digit", minute: "2-digit",
          })}
        </span>
        <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
          {o.duration_ms} ms
        </span>
        {!o.ok && o.error && (
          <span className="ml-auto truncate text-[11px] text-red-600 dark:text-red-400">
            {o.error}
          </span>
        )}
      </button>
      {open && (
        <div className="space-y-1 border-t border-border bg-muted/30 px-3 py-2">
          {o.output && (
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap font-mono text-[11px]">
              {o.output}
              {truncated && (
                <span className="text-muted-foreground"> … (full output is {o.output_full_len} bytes)</span>
              )}
            </pre>
          )}
          {o.error && (
            <pre className="max-h-32 overflow-auto whitespace-pre-wrap font-mono text-[11px] text-red-700 dark:text-red-400">
              {o.error}
            </pre>
          )}
          {!o.output && !o.error && (
            <span className="text-[11px] text-muted-foreground">(empty output)</span>
          )}
        </div>
      )}
    </div>
  );
}

function ConnectorDataPanel({
  device, connector,
}: {
  device: Device;
  connector: DeviceConnector;
}) {
  const Icon = connector.kind === "http_poll" ? Activity : Terminal;
  const recent = connector.recent_outputs ?? [];

  // Stable summary line: how many entries this connector schedules.
  const entries = useMemo(() => {
    const cfg = (connector.config ?? {}) as Record<string, unknown>;
    if (connector.kind === "shell_command") {
      const cmds = (cfg.commands as Array<{ command: string; interval_s: number }>) ?? [];
      return cmds.map((c) => `${c.command}  (every ${c.interval_s}s)`);
    }
    if (connector.kind === "http_poll") {
      const polls = (cfg.polls as Array<{ url: string; interval_s: number }>) ?? [];
      return polls.map((p) => `${p.url}  (every ${p.interval_s}s)`);
    }
    return [];
  }, [connector.config, connector.kind]);

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-semibold">
          {connector.kind === "http_poll" ? "HTTP poll" : "Shell command"}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {entries.length} {entries.length === 1 ? "entry" : "entries"}
        </span>
        <span className={cn(
          "ml-auto text-[11px] font-medium uppercase",
          connector.status === "active" ? "text-emerald-700 dark:text-emerald-400" :
          connector.status === "failing" ? "text-red-600 dark:text-red-400" :
                                            "text-muted-foreground",
        )}>
          {connector.status}
        </span>
      </div>

      {entries.length > 0 && (
        <div className="border-b border-border px-4 py-2">
          {entries.map((e, i) => (
            <pre key={i} className="overflow-x-auto whitespace-pre font-mono text-[11px] text-muted-foreground">
              {e}
            </pre>
          ))}
        </div>
      )}

      {connector.last_error && (
        <div className="border-b border-border bg-red-500/5 px-4 py-2 text-[11px] text-red-700 dark:text-red-400">
          {connector.last_error}
        </div>
      )}

      {recent.length === 0 ? (
        <div className="px-4 py-4 text-center text-[11px] text-muted-foreground">
          No runs yet — runner will pick this up within a minute.
        </div>
      ) : (
        <div>
          {recent.map((o) => <OutputRow key={o.id} o={o} />)}
          <ConnectorOutputsExpander deviceId={device.id} connectorId={connector.id ?? 0} />
        </div>
      )}
    </div>
  );
}

function ConnectorOutputsExpander({
  deviceId, connectorId,
}: { deviceId: number; connectorId: number }) {
  const [open, setOpen] = useState(false);
  const q = useConnectorOutputs(deviceId, connectorId, open);
  return (
    <div className="border-t border-border px-3 py-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] font-medium text-primary hover:underline"
      >
        {open ? "Hide history" : "Show last 20 runs"}
      </button>
      {open && q.isLoading && (
        <div className="mt-2 text-[11px] text-muted-foreground">Loading…</div>
      )}
      {open && q.data && (
        <div className="mt-2">
          {q.data.items.map((o) => <OutputRow key={o.id} o={o} />)}
        </div>
      )}
    </div>
  );
}

function DecidedProposalRow({ p }: { p: CapabilityProposal }) {
  const tone =
    p.status === "accepted" ? "text-emerald-700 dark:text-emerald-400" :
    p.status === "skipped"  ? "text-muted-foreground" :
                              "text-amber-700 dark:text-amber-400";
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border py-2 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm">{p.title}</div>
        <div className="text-[11px] text-muted-foreground">
          {CONNECTOR_LABEL[p.kind] ?? p.kind} · {relativeTime(p.decided_at)}
        </div>
      </div>
      <span className={cn("text-[11px] font-medium uppercase tracking-wide", tone)}>{p.status}</span>
    </div>
  );
}

export default function DeviceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const deviceId = id ? Number(id) : null;
  const q = useDevice(deviceId);
  const [addConnectorOpen, setAddConnectorOpen] = useState(false);

  const device = q.data;
  const pending = device?.pending_proposals ?? [];
  const decided = device?.decided_proposals ?? [];
  // Only periodic kinds produce runner output rows worth showing here.
  const periodic = (device?.connectors ?? []).filter(
    (c) => (c.kind === "shell_command" || c.kind === "http_poll") && c.id !== undefined,
  );

  if (q.isLoading || !device) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="space-y-6">
      <DeviceHeader device={device} onBack={() => navigate("/devices")} />

      <ConnectorsSection device={device} onAdd={() => setAddConnectorOpen(true)} />
      <AddConnectorSheet
        device={device}
        open={addConnectorOpen}
        onOpenChange={setAddConnectorOpen}
      />

      <ScoutPanel device={device} />

      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-semibold">
            Pending proposals
            {pending.length > 0 && (
              <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                {pending.length}
              </span>
            )}
          </h2>
        </div>
        {pending.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/20 p-6 text-center">
            <p className="text-sm text-muted-foreground">
              {device.last_scouted_at
                ? "No pending proposals. Re-scout to look again."
                : "Run the capability scout to get suggestions."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map((p) => (
              <ProposalCard key={p.id} p={p} deviceId={device.id} />
            ))}
          </div>
        )}
      </div>

      {periodic.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold">Live data</h2>
          <div className="space-y-3">
            {periodic.map((c) => (
              <ConnectorDataPanel key={c.id} device={device} connector={c} />
            ))}
          </div>
        </div>
      )}

      {decided.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-muted-foreground">
            History <span className="font-normal">({decided.length} decided)</span>
          </h2>
          <div className="rounded-lg border border-border bg-card px-4">
            {decided.map((p) => <DecidedProposalRow key={p.id} p={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}
